import { useState, useEffect } from "react"
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  Cross1Icon,
} from "@radix-ui/react-icons"
import { Document, Page, pdfjs } from "react-pdf"
import "react-pdf/dist/Page/TextLayer.css"
import "react-pdf/dist/Page/AnnotationLayer.css"
import TextButton from "@common/ui/TextButton"
import styled from "styled-components/macro"
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`

type PDFViewerProps = {
  pageDisplay: "single" | "double"
  pfdFile: string
  title: string
  setSelectedPdf: (pdf: any) => void
}

export default function PDFViewer(props: PDFViewerProps) {
  const [numPages, setNumPages] = useState<number>()
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [height, setHeight] = useState(0)

  useEffect(() => {
    const updateHeight = () => {
      const isSmallScreen = window.matchMedia("(max-width: 1366px)").matches
      const newHeight = isSmallScreen
        ? window.innerHeight / 1.25
        : window.innerHeight / 1.15
      setHeight(newHeight)
    }

    updateHeight()

    window.addEventListener("resize", updateHeight)

    return () => {
      window.removeEventListener("resize", updateHeight)
    }
  }, [])

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setNumPages(numPages)
  }

  const { pageDisplay, pfdFile, title, setSelectedPdf } = props

  const prevButtonDisabled = pageNumber <= 1
  const nextButtonDisabled =
    pageDisplay === "single"
      ? pageNumber >= numPages
      : pageNumber >= numPages - 1

  return (
    <>
      <PDFHeader>
        <TextButton
          color="secondary"
          className="close-button"
          onClick={() => setSelectedPdf(null)}
          icon={<Cross1Icon color="white" />}
          iconPosition="right"
          variant="outline"
        />
        <h2>{title}</h2>
      </PDFHeader>
      <Document
        className="pdf-document"
        file={pfdFile}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={console.error}
      >
        <Page className="pdf-page" pageNumber={pageNumber} height={height} />
        {pageDisplay === "double" && (
          <Page
            className="pdf-page"
            pageNumber={pageNumber + 1}
            height={height}
          />
        )}
      </Document>
      <div className="pdf-page-controls">
        <TextButton
          variant="fill"
          color="#000000"
          icon={<ChevronLeftIcon />}
          disabled={prevButtonDisabled}
          onClick={() => setPageNumber(pageNumber - 1)}
          className="border"
        />
        <TextButton
          variant="fill"
          color="#000000"
          icon={<ChevronRightIcon />}
          disabled={nextButtonDisabled}
          onClick={() => setPageNumber(pageNumber + 1)}
          className="border"
        />
      </div>

      <PaginationInfo>
        Page {pageNumber}
        {pageDisplay === "double" && !nextButtonDisabled
          ? `-${pageNumber + 1}`
          : ""}{" "}
        of {numPages}
      </PaginationInfo>
    </>
  )
}

const PaginationInfo = styled.div`
  font-size: 1rem;
  color: var(--secondary);
  width: 100%;
  height: 7.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
`

const PDFHeader = styled.div`
  position: relative;
  width: 100%;
  height: 7.5rem;
  display: flex;
  justify-content: center;
  align-items: center;

  .close-button {
    position: absolute;
    top: 0;
    left: 0;
    margin: 1.25rem 2.5rem;
    border-radius: 5px;
    z-index: 1;
    background-color: var(--secondary);
  }

  h2 {
    font-size: 1.5rem;
    font-weight: 500;
    color: var(--secondary);
  }
`
